import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { TopBar } from './TopBar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { Sidebar } from '@/components/sidebar/Sidebar';
import { useAppDispatch, useAppSelector, selectBadges } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { fetchConversations } from '@/store/chat/chatSlice';
import { AddEventMenuButton } from '@/components/calendar/AddEventMenuButton';
import { useVariant } from '@/contexts/VariantContext';
import { getCalendarEventTypes } from '@/services/calendar.service';
import { CalendarEventType } from '@/types/calendar';
import { SidebarMode } from '@/components/sidebar/sidebar.types';
import { MODES } from '@/components/sidebar/sidebar.config';

const DRAWER_WIDTH = 200;
const DRAWER_WIDTH_COLLAPSED = 64;

interface DashboardLayoutProps {
  children: React.ReactNode;
  'data-i-doc'?: string;
  onChangePassword?: () => void;
  onAddEventSelected?: (eventTypeCode: string) => void;
  eventTypes?: CalendarEventType[];
  disableContentPadding?: boolean;
  rightAction?: React.ReactNode;
  titleOverride?: string;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  'data-i-doc': dataIDoc,
  onChangePassword,
  onAddEventSelected,
  eventTypes: propEventTypes,
  disableContentPadding = false,
  rightAction,
  titleOverride,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAuth();
  const conversationsFetchedRef = useRef(false);

  const [activeSidebarMode, setActiveSidebarMode] = useState<SidebarMode>('general');

  const [internalEventTypes, setInternalEventTypes] = useState<CalendarEventType[]>([]);
  const [loadingEventTypes, setLoadingEventTypes] = useState(false);
  const [errorEventTypes, setErrorEventTypes] = useState<string | null>(null);

  const isClient = user?.roles?.includes('client');
  const isDashboardPage = location.pathname === '/dashboard';
  const { variant } = useVariant();

  useEffect(() => {
    if (isAuthenticated && !conversationsFetchedRef.current) {
      dispatch(fetchConversations());
      conversationsFetchedRef.current = true;
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (isClient && !propEventTypes) {
      const fetchEventTypes = async () => {
        try {
          setLoadingEventTypes(true);
          const types = await getCalendarEventTypes();
          setInternalEventTypes(types);
        } catch (err) {
          console.error('Error fetching calendar event types:', err);
          setErrorEventTypes('Не вдалося завантажити типи подій.');
        } finally {
          setLoadingEventTypes(false);
        }
      };
      fetchEventTypes();
    }
  }, [propEventTypes, isClient]);

  // Запам'ятовування позиції скролу (2026-08-20, за проханням користувача — "я втомився
  // перемотувати кожен раз"). Скролиться саме `window`/document, не `<main>` — той не має власної
  // висоти й overflow (overflowX: 'hidden' вище, вертикальна вісь навмисно НЕ обрізана, щоб
  // sticky-елементи працювали в звичайному потоці сторінки). Ключ — шлях+query (різні таби
  // `?tab=...` на одній сторінці мають різний контент і свою позицію), sessionStorage — позиція
  // актуальна лише для цієї вкладки браузера, не постійна нстройка.
  const scrollKey = `scroll-pos:${location.pathname}${location.search}`;
  useEffect(() => {
    // Вимикаємо нативне відновлення скролу браузера (2026-08-20, знайдено — "не працює автоскрол
    // до того місця") — на F5 браузер сам намагається відновити позицію ще до того, як React
    // домалює async-контент (напр. Simulation-таб EHI вантажить дані через API), і потім наш
    // ефект нижче або не спрацьовує, або конфліктує з нативним. Керуємо відновленням самі.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    let restoreTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let rafId: number | null = null;
    const raw = sessionStorage.getItem(scrollKey);
    const y = raw ? Number(raw) : NaN;
    if (!Number.isNaN(y) && y > 0) {
      // Одноразового scrollTo одразу після маунта не досить — сторінка (особливо з
      // async-контентом) часто ще коротша за збережену позицію в цей момент. Пробуємо
      // повторно, поки документ не стане досить високим, до ~1.5с, тоді припиняємо.
      let attempts = 0;
      const tryRestore = () => {
        attempts += 1;
        window.scrollTo(0, y);
        const tallEnough = document.documentElement.scrollHeight - window.innerHeight >= y - 2;
        if (!tallEnough && attempts < 15) {
          restoreTimeoutId = setTimeout(tryRestore, 100);
        }
      };
      rafId = requestAnimationFrame(tryRestore);
    } else {
      // Кодревью (2026-08-20): без цього перший перехід на новий маршрут (ще без свого ключа в
      // sessionStorage) лишав вікно проскрольненим на позицію попередньої сторінки — нативне
      // scrollRestoration вимкнене вище, а SPA-навігація сама по собі скрол не скидає.
      window.scrollTo(0, 0);
    }
    let saveTimeout: ReturnType<typeof setTimeout> | null = null;
    const handleScroll = () => {
      if (saveTimeout) clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        sessionStorage.setItem(scrollKey, String(window.scrollY));
      }, 150);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (saveTimeout) clearTimeout(saveTimeout);
      if (restoreTimeoutId) clearTimeout(restoreTimeoutId);
      // Кодревью (2026-08-20): rAF раніше не скасовувався — при швидкій навігації міг спрацювати
      // вже після маунту ефекту наступної сторінки й проскролити ЇЇ на Y попередньої.
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollKey]);

  const eventTypes = propEventTypes || internalEventTypes;
  const isLoading = loadingEventTypes && !propEventTypes;
  const error = errorEventTypes && !propEventTypes ? errorEventTypes : null;

  const badges = useAppSelector(selectBadges);
  const totalUnreadCount = badges.chat;

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = sessionStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerWidth = isCollapsed && !isMobile ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const activeModeConfig = MODES[activeSidebarMode];

  const sidebarContent = (
    <Sidebar
      isCollapsed={isCollapsed}
      isMobile={isMobile}
      onToggle={handleDrawerToggle}
      onNavigate={handleNavigate}
      onModeChange={setActiveSidebarMode}
      chatUnreadCount={totalUnreadCount}
    />
  );

  return (
    <>
      <ImpersonationBanner />
      <Box sx={{ display: 'flex' }}>
        <TopBar
          drawerWidth={drawerWidth}
          onMobileMenuToggle={handleMobileMenuToggle}
          onChangePassword={onChangePassword}
          rightAction={rightAction}
          titleOverride={titleOverride}
          backgroundColor={activeModeConfig.bgColor}
          textColor={activeModeConfig.textColor}
        />

        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
          >
            {sidebarContent}
          </Drawer>

          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                transition: theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
            }}
            open
          >
            {sidebarContent}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: disableContentPadding ? 0 : { xs: 1.5, sm: 2, md: 3 },
            width: { md: `calc(100% - ${drawerWidth}px)` },
            // Було `overflow: 'hidden'` (обидві осі) — ламало `position: sticky` у звичайному
            // потоці сторінки для будь-якої дочірньої сторінки (2026-08-21, знайдено на
            // EHI-калькуляторі): `overflow: hidden` створює CSS "scroll container", відносно
            // якого рахується sticky, а сам `<main>` не має фіксованої висоти й ніколи не
            // скролиться — тому sticky всередині поводився як static. `overflowX: hidden`
            // зберігає те, заради чого це, скоріш за все, додавали (захист від горизонтального
            // скролу сторінки), не займаючи вертикальну вісь.
            overflowX: 'hidden',
          }}
        >
          <Box sx={{ height: '52px', flexShrink: 0 }} />
          <Box data-i-doc={dataIDoc}>
            {children}
          </Box>
        </Box>
      </Box>

      {isClient && isDashboardPage && variant === 'a' && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: `calc(50% + ${drawerWidth / 2}px)`,
            transform: 'translateX(-50%)',
            zIndex: 1200,
          }}
        >
          <AddEventMenuButton
            eventTypes={eventTypes}
            onEventTypeSelect={(eventType) => {
              onAddEventSelected?.(eventType.code);
            }}
            loading={isLoading}
            error={error}
          />
        </Box>
      )}
    </>
  );
};
